import { useCallback, useEffect, useMemo, useState } from "react";
import type { RuleGroupType } from "react-querybuilder";
import "react-querybuilder/dist/query-builder.scss";
import { QueryBuilder } from "react-querybuilder";
import { QueryBuilderMaterial } from "@react-querybuilder/material";
import {
  Box,
  IconButton,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  OutlinedInput,
  InputAdornment,
  Alert,
  Chip,
  Tooltip,
} from "@mui/material";
import { Close, FilterAltOutlined, BookmarkBorderOutlined, RestartAlt } from "@mui/icons-material";
import { useLocation } from "react-router-dom";
import { enqueueSnackbar } from "notistack";
import _ from "lodash";

import "./filter.scss";
import Select from "../select/select";
import Typography from "../typography/typography";
import Button from "../button/button";
import SearchBar from "../search-bar/search-bar";
import SaveFilterModal from "../save-filter-modal/save-filter-modal";
import { Edit, Trash } from "../icons";

import CombinatorSelect from "./components/combinator-select";
import RemoveAction from "./components/remove-action";
import FieldSelect from "./components/field-select";
import AddFilterButton from "./components/add-filter";
import AddGroupButton from "./components/add-group";
import ValueEditor from "./components/value-editor";
import OperatorSelect from "./components/operator-select";
import RemoveGroupAction from "./components/delete-group";

import { putV1FilterId, deleteV1FilterId } from "../../api-client/api.system-feature/api";
import type * as Types from "../../api-client/api.system-feature/api-types";
import { auth } from "../../constants/auth";
import { generateFields, generateQueryString, getErrorMessage } from "../../utils/common";
import { usePages } from '@erpsquad/common/runtime';
import { useLanguage } from '@erpsquad/common/runtime';
import { IResources, routeToResourceMap } from "../../constants/route-page-map";

interface SavedFilter {
  id: string;
  name: string;
  query: RuleGroupType;
}

interface IFilter {
  open: boolean;
  onClose: () => void;
  activeView?: string;
  savedFilters?: SavedFilter[];
  fetchSavedFilters?: () => Promise<void>;
  setFilterBar?: any;
  onSaveFilter?: (query: RuleGroupType) => void;
  onSelectedFilter?: (filterName: SavedFilter | null) => void;
  fields: any;
  applyFilter: any;
  isUseFields?: boolean;
}

const INITIAL_QUERY: RuleGroupType = { combinator: "and", rules: [] };

/* ---------- pure helpers (extracted, testable) ---------- */

const hasDuplicateAndFields = (group: RuleGroupType): boolean => {
  if (!group?.rules?.length) return false;
  if (group.combinator === "and") {
    const seen = new Set<string>();
    for (const rule of group.rules) {
      if ("rules" in rule) {
        if (hasDuplicateAndFields(rule as RuleGroupType)) return true;
        continue;
      }
      const field = (rule as any).field;
      if (!field) continue;
      if (seen.has(field)) return true;
      seen.add(field);
    }
  }
  return (group.rules as any[]).some((r) =>
    "rules" in r ? hasDuplicateAndFields(r as RuleGroupType) : false
  );
};

const isRuleInvalid = (rule: any): boolean => {
  if (Array.isArray(rule?.rules)) {
    return rule.rules.length === 0 || rule.rules.some(isRuleInvalid);
  }
  if (!rule?.field) return true;
  if (["null", "notNull"].includes(rule?.operator)) return false;
  return _.isEmpty(rule?.value);
};

const countActiveRules = (group: RuleGroupType): number => {
  if (!group?.rules?.length) return 0;
  return (group.rules as any[]).reduce((n, r) => {
    if ("rules" in r) return n + countActiveRules(r as RuleGroupType);
    return n + (isRuleInvalid(r) ? 0 : 1);
  }, 0);
};

/* ---------- component ---------- */

const Filter = ({
  open,
  onClose,
  savedFilters,
  fetchSavedFilters,
  onSaveFilter,
  onSelectedFilter,
  applyFilter,
  fields: incomingFields = [],
  isUseFields = false,
}: IFilter) => {
  const location = useLocation();
  const token = localStorage.getItem(auth.storageTokenKeyName);
  const { updatePageInfo, activePage, schemaFields, setActivePage, customRouteToResourceMap } = usePages();
  const { isRtl } = useLanguage();

  const resources: IResources = useMemo(() => {
    const mergedMap = { ...routeToResourceMap, ...customRouteToResourceMap };
    return mergedMap[location.pathname] || null;
  }, [location.pathname, customRouteToResourceMap])

  // Don't reassign props. Derive a stable, memoized fields array.
  const fields = useMemo(() => {
    if (isUseFields) return generateFields(incomingFields);
    if (schemaFields?.length) return generateFields(schemaFields);
    return incomingFields;
  }, [isUseFields, incomingFields, schemaFields]);

  const [query, setQuery] = useState<RuleGroupType>(INITIAL_QUERY);
  const [filteredSavedFilters, setFilteredSavedFilters] = useState<SavedFilter[]>(
    savedFilters ?? []
  );
  const [selectedFilter, setSelectedFilter] = useState<SavedFilter | null>(null);
  const [openRenameFilter, setIsOpenRenameFilter] = useState<SavedFilter | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (savedFilters) setFilteredSavedFilters(savedFilters);
  }, [savedFilters]);

  useEffect(() => {
    const next = !_.isEmpty(activePage.filters) ? activePage.filters : INITIAL_QUERY;
    setQuery(next);
    const sf = savedFilters?.find((f) => f.name === activePage?.filters?.name) || null;
    setSelectedFilter(sf);
  }, [activePage?.filters, savedFilters]);

  /* ---------- derived state ---------- */

  const activeCount = useMemo(() => countActiveRules(query), [query]);
  const hasInvalidRule = useMemo(() => isRuleInvalid(query), [query]);
  const isDisabled = Boolean(hasInvalidRule || errorMessage);

  /* ---------- handlers ---------- */

  const handleQueryChange = useCallback(
    (q: RuleGroupType) => {
      const updatedRules = q.rules.map((rule: any, i: number) => {
        const prev = (query.rules as any[])?.[i];
        const fieldChanged = rule.field !== prev?.field;
        const isNullOp = ["null", "notNull"].includes(rule.operator);
        return {
          ...rule,
          value: fieldChanged || isNullOp ? "" : rule.value,
        };
      });

      const nextQuery = { ...q, rules: updatedRules };
      setQuery(nextQuery);

      // Re-validate on every change so error messages don't go stale
      if (errorMessage) {
        const stillDuplicate = hasDuplicateAndFields(nextQuery);
        if (!stillDuplicate) setErrorMessage(null);
      }

      onSaveFilter?.(nextQuery);
    },
    [query.rules, errorMessage, onSaveFilter]
  );

  const handleApplyFilter = useCallback(
    (clear = false) => {
      if (clear) {
        setQuery(INITIAL_QUERY);
        setSelectedFilter(null);
        setErrorMessage(null);
        updatePageInfo({ filters: INITIAL_QUERY, filterQueryString: "" });
        onClose();
        return;
      }

      if (hasDuplicateAndFields(query)) {
        setErrorMessage("Duplicate fields detected in an AND group. Each field can only appear once per AND group.");
        return;
      }

      const generatedSQLQuery = generateQueryString(query);
      updatePageInfo({ filters: query, filterQueryString: generatedSQLQuery });
      onClose();
    },
    [query, updatePageInfo, onClose]
  );

  const handleFilterSwitch = (sf?: SavedFilter | null) => {
    if (sf) {
      setSelectedFilter(sf);
      setQuery(sf.query);
      onSelectedFilter?.(sf);
    } else {
      setSelectedFilter(null);
      setQuery(INITIAL_QUERY);
      onSelectedFilter?.(null);
    }
  };

  const handleSearchEvent = (searchText: string) => {
    const term = searchText.toLowerCase().trim();
    setFilteredSavedFilters(
      term ? (savedFilters ?? []).filter((f) => f.name.toLowerCase().includes(term)) : savedFilters ?? []
    );
  };

  const handleRenameFilter = async (filterId: string, newName: string) => {
    const req: Types.PutV1FilterIdRequest = {
      "x-token": token,
      id: filterId,
      name: newName,
      module: resources?.fieldResource || resources?.pageResource,
    };
    try {
      await putV1FilterId(req);
      await fetchSavedFilters?.();
      enqueueSnackbar("Filter renamed successfully.");
      return true;
    } catch (error) {
      const msg = error instanceof Error ? getErrorMessage(error.message) : "Something went wrong while saving the filter";
      enqueueSnackbar(msg, { variant: "error" });
      return false;
    }
  };

  const handleUpdateSelectedFilterQuery = async () => {
    if (!selectedFilter) return;
    try {
      await putV1FilterId({
        "x-token": token,
        id: selectedFilter.id,
        data: query,
      } as Types.PutV1FilterIdRequest);
      await fetchSavedFilters?.();
      setActivePage((prev: any) => ({
        ...prev,
        filters: { ...prev.filters, rules: query.rules, combinator: query.combinator, name: selectedFilter.name },
      }));
      enqueueSnackbar("Filter updated successfully.");
    } catch {
      enqueueSnackbar("Error updating saved filter.", { variant: "error" });
    }
  };

  const handleDeleteFilter = async (filterId: string) => {
    try {
      await deleteV1FilterId({ "x-token": token, id: filterId } as Types.DeleteV1FilterIdRequest);
      setSelectedFilter(null);
      setQuery(INITIAL_QUERY);
      await fetchSavedFilters?.();
      enqueueSnackbar("Filter deleted.");
    } catch {
      enqueueSnackbar("Error deleting saved filter.", { variant: "error" });
    }
  };

  /* ---------- render ---------- */

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2, overflow: "hidden" } }}
      className="filter-modal"
    >
      <DialogTitle className="filter-header">
        <Box className="filter-header__title">
          <Box className="filter-header__icon">
            <FilterAltOutlined fontSize="small" />
          </Box>
          <Box>
            <Typography type="s3" weight="medium" color="theme.secondary.1000">
              Filters
            </Typography>
            <Typography type="s5" color="theme.secondary.700">
              {activeCount} active {activeCount === 1 ? "condition" : "conditions"}
            </Typography>
          </Box>
        </Box>

        <Box className="filter-header__actions">
          <Select
            size="small"
            className="filter-dropdown"
            value={selectedFilter ? selectedFilter.name : "Saved Filters"}
            onChange={(e: any) => {
              const sf = savedFilters?.find((f) => f.name === e.target.value);
              handleFilterSwitch(sf);
            }}
            variant="outlined"
            onClose={() => setTimeout(() => handleSearchEvent(""), 300)}
            startAdornment={
              <InputAdornment position="start" sx={{ ml: 1, mr: -0.5 }}>
                <BookmarkBorderOutlined fontSize="small" />
              </InputAdornment>
            }
            input={
              selectedFilter ? (
                <OutlinedInput
                  label=""
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="clear selection"
                        onClick={() => handleFilterSwitch()}
                        edge="end"
                        sx={{ mr: 1.5 }}
                        size="small"
                      >
                        <Close sx={{ fontSize: "1rem" }} />
                      </IconButton>
                    </InputAdornment>
                  }
                />
              ) : undefined
            }
          >
            <div className="searchbar-box-list">
              <SearchBar placeholder="Search filters" handleSearch={handleSearchEvent} />
            </div>
            {filteredSavedFilters?.length ? (
              filteredSavedFilters.map((sf) => (
                <MenuItem
                  key={sf.id}
                  value={sf.name}
                  className="menu-list"
                  selected={selectedFilter?.name === sf.name}
                >
                  <Typography color="theme.secondary.1000" type="s3">
                    {sf.name}
                  </Typography>
                  <div className="icon-list">
                    <Tooltip title="Rename">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setIsOpenRenameFilter(sf);
                        }}
                      >
                        <Edit
                          fontSize="small"
                          color={selectedFilter?.name === sf.name ? "theme.primary.800" : "inherit"}
                        />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleDeleteFilter(sf.id);
                        }}
                        color="error"
                      >
                        <Trash fontSize="small" color="theme.error.600" />
                      </IconButton>
                    </Tooltip>
                  </div>
                </MenuItem>
              ))
            ) : (
              <Box className="empty-saved-filters">
                <Typography type="s4" color="theme.secondary.700">
                  No saved filters
                </Typography>
              </Box>
            )}
          </Select>

          <SaveFilterModal
            open={openRenameFilter !== null}
            onClose={() => setIsOpenRenameFilter(null)}
            handleSubmit={(name) => (openRenameFilter ? handleRenameFilter(openRenameFilter.id, name) : Promise.resolve(false))}
            oldName={openRenameFilter?.name}
          />

          <IconButton size="small" onClick={onClose} aria-label="Close filters">
            <Close fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent className="filter-content">
        {errorMessage && (
          <Alert severity="error" className="filter-error" onClose={() => setErrorMessage(null)}>
            <Typography color="error" type="s4">
              {errorMessage}
            </Typography>
          </Alert>
        )}

        <Box className="query-builder-wrapper">
          <QueryBuilderMaterial>
            <QueryBuilder
              fields={fields || []}
              query={query}
              onQueryChange={handleQueryChange}
              showCombinatorsBetweenRules
              controlClassnames={{ header: "actions" }}
              controlElements={{
                addRuleAction: AddFilterButton,
                addGroupAction: AddGroupButton,
                combinatorSelector: CombinatorSelect,
                fieldSelector: FieldSelect,
                operatorSelector: OperatorSelect,
                removeRuleAction: RemoveAction,
                valueEditor: ValueEditor,
                removeGroupAction: RemoveGroupAction,
              }}
            />
          </QueryBuilderMaterial>
        </Box>
      </DialogContent>

      <DialogActions className="filter-footer">
        <Button
          variant="text"
          startIcon={<RestartAlt fontSize="small" />}
          className={`clear-all-btn ${isRtl ? "clear-all-btn--rtl" : ""}`}
          onClick={() => handleApplyFilter(true)}
          disabled={activeCount === 0}
        >
          Clear all filters
        </Button>

        <Box className="filter-footer__actions">
          {selectedFilter && (
            <Button
              variant="outlined"
              className="update-filter-btn"
              onClick={handleUpdateSelectedFilterQuery}
              disabled={isDisabled}
            >
              Update selected filter
            </Button>
          )}
          <Button variant="outlined" className="cancel-filter-btn" onClick={onClose}>
            Cancel
          </Button>
          <Button
            className="apply-filter-btn"
            disabled={isDisabled}
            onClick={() => handleApplyFilter()}
            endIcon={
              activeCount > 0 ? (
                <Chip
                  label={activeCount}
                  size="small"
                  className="apply-count-chip"
                />
              ) : null
            }
          >
            Apply
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export { Filter };
export default Filter;